import React, { useState, useRef, useEffect } from 'react';
import { useAgentSession } from '../../contexts/AgentSessionContext';
import { Send, Sparkles, Loader2 } from 'lucide-react';

const QUICK_PROMPTS = [
  'Load my portfolio for Customer ID 1001',
  'What is my total invested capital and PnL?',
  'Analyze INFY.NS valuation with 10% discount rate',
  'Compare scenarios: Tech Sector Correction vs Rate Hike Shock',
];

export const ChatInput: React.FC = () => {
  const { sendMessage, isStreaming } = useAgentSession();
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Refocus only on fine pointers: on touch devices focusing pops the keyboard over the new answer.
  useEffect(() => {
    const hasFinePointer = window.matchMedia?.('(pointer: fine)').matches ?? true;
    if (!isStreaming && hasFinePointer && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isStreaming]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!input.trim() || isStreaming) return;
    sendMessage(input.trim());
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChipClick = (prompt: string) => {
    if (isStreaming) return;
    sendMessage(prompt);
  };

  return (
    <div className="chat-input-container">
      <div className="quick-prompts-bar">
        <Sparkles size={14} className="sparkles-icon" />
        <span className="quick-label">Suggestions:</span>
        <div className="quick-chips-scroll">
          {QUICK_PROMPTS.map((prompt, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleChipClick(prompt)}
              disabled={isStreaming}
              className="prompt-chip"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="input-form">
        <textarea
          ref={textareaRef}
          id="chatPromptInput"
          aria-label="Message prompt for Finance Advisor"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isStreaming ? 'Agent is analyzing and synthesizing...' : 'Ask about your portfolio, PnL, company research, or DCF valuation (Enter to send)...'}
          disabled={isStreaming}
          rows={2}
          enterKeyHint="send"
          className="chat-textarea"
        />

        <button
          type="submit"
          disabled={!input.trim() || isStreaming}
          className="send-button"
          title="Send message to Finance Advisor"
          aria-label="Send message to Finance Advisor"
        >
          {isStreaming ? (
            <Loader2 size={18} className="spin text-accent" />
          ) : (
            <Send size={18} />
          )}
        </button>
      </form>
    </div>
  );
};
